import { useTranslation } from 'react-i18next';
import { commercialLawItems } from '@/mocks/commercialLawData';

export default function CommercialLawContent() {
  const { t } = useTranslation();

  const scrollToItem = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Preamble */}
      <section className="bg-background-50 pt-16 pb-8">
        <div className="max-w-[760px] mx-auto px-6 md:px-8">
          <p className="text-[16px] md:text-[17px] text-foreground-700 tracking-[0.04em] leading-[2.2]">
            {t('commercialLaw.content.preamble')}
          </p>
        </div>
      </section>

      {/* Table of Contents */}
      <section className="bg-background-50 pb-10">
        <div className="max-w-[760px] mx-auto px-6 md:px-8">
          <h2 className="font-heading text-lg md:text-xl text-foreground-950 tracking-[0.06em] mb-2">
            {t('commercialLaw.toc.heading')}
          </h2>
          <p className="text-accent-500 text-[13px] md:text-[14px] tracking-[0.12em] mb-5 font-label">
            CONTENTS
          </p>
          <div className="w-8 h-[1px] bg-accent-500/40 mb-8"></div>

          <ol className="space-y-1.5">
            {commercialLawItems.map((item, index) => (
              <li key={item.id}>
                <button
                  onClick={() => scrollToItem(item.id)}
                  className="text-left text-[16px] md:text-[17px] text-foreground-700 hover:text-accent-500 transition-colors tracking-[0.04em] leading-relaxed cursor-pointer border-b border-background-200/60 pb-2 w-full"
                >
                  <span className="text-accent-500 font-label mr-3">
                    {String(index + 1).padStart(2, '0')}.
                  </span>
                  {t(`commercialLaw.i.${item.id.replace('item-', '')}.label`, { defaultValue: item.label })}
                </button>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-[760px] mx-auto px-6 md:px-8">
        <div className="border-t border-background-200/60"></div>
      </div>

      {/* Definition List Items */}
      {commercialLawItems.map((item, index) => {
        const itemKey = item.id.replace('item-', '');
        return (
          <section key={item.id} id={item.id} className="bg-background-50 pb-14">
            <div className="max-w-[760px] mx-auto px-6 md:px-8">
              <div className={index === 0 ? 'pt-14' : 'border-t border-background-200/60 pt-14'}>
                <div className="flex items-baseline gap-3 mb-2">
                  <span className="text-accent-500 text-[15px] font-label tracking-[0.08em] whitespace-nowrap">
                    {String(index + 1).padStart(2, '0')}.
                  </span>
                  <h2 className="font-heading text-lg md:text-xl text-foreground-950 tracking-[0.06em]">
                    {t(`commercialLaw.i.${itemKey}.label`, { defaultValue: item.label })}
                  </h2>
                </div>
                <div className="w-8 h-[1px] bg-accent-500/40 mb-6"></div>

                <div className="bg-background-100/70 px-6 py-5 border border-background-200/50">
                  <div className="space-y-3">
                    {item.content.map((paragraph, pIdx) => (
                      <p
                        key={pIdx}
                        className={`text-[16px] md:text-[17px] tracking-[0.04em] leading-[2.2] ${
                          pIdx === 0
                            ? 'text-foreground-900 font-label'
                            : 'text-foreground-700'
                        }`}
                      >
                        {t(`commercialLaw.i.${itemKey}.p.${pIdx}`, { defaultValue: paragraph })}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      {/* Note */}
      <section className="bg-background-50 pb-14">
        <div className="max-w-[760px] mx-auto px-6 md:px-8">
          <div className="border-t border-background-200/60 pt-14">
            <h2 className="font-heading text-lg md:text-xl text-foreground-950 tracking-[0.06em] mb-2">
              {t('commercialLaw.content.noteTitle')}
            </h2>
            <p className="text-accent-500 text-[13px] md:text-[14px] tracking-[0.12em] mb-5 font-label">
              NOTE
            </p>
            <div className="w-8 h-[1px] bg-accent-500/40 mb-6"></div>

            <p className="text-[16px] md:text-[17px] text-foreground-700 tracking-[0.04em] leading-[2.2]">
              {t('commercialLaw.content.noteText')}
            </p>
          </div>
        </div>
      </section>

      {/* Enactment Date */}
      <section className="bg-background-50 pb-20">
        <div className="max-w-[760px] mx-auto px-6 md:px-8">
          <div className="border-t border-background-200/60 pt-10">
            <div className="bg-background-100/70 px-6 py-5 border border-background-200/50">
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8">
                <span className="text-[15px] font-label text-foreground-500 tracking-[0.06em] whitespace-nowrap">
                  {t('commercialLaw.content.enactDate')}
                </span>
                <span className="text-[16px] text-foreground-700 tracking-[0.04em]">
                  {t('commercialLaw.content.enactDateValue')}
                </span>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-8 mt-3">
                <span className="text-[15px] font-label text-foreground-500 tracking-[0.06em] whitespace-nowrap">
                  {t('commercialLaw.content.lastRevised')}
                </span>
                <span className="text-[16px] text-foreground-700 tracking-[0.04em]">
                  {t('commercialLaw.content.lastRevisedValue')}
                </span>
              </div>
            </div>
            <p className="text-[14px] text-foreground-400 tracking-[0.04em] mt-4 leading-relaxed">
              {t('commercialLaw.content.amendNotice')}
            </p>
          </div>
        </div>
      </section>
    </>
  );
}
